import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {User, UserCheck, Lock, Info, Save, DollarSign, Plus, Trash2, Calendar, Edit} from 'lucide-react'
import toast from "react-hot-toast"

function Members() {
  const [user, setUser] = useState(lumi.auth.user)
  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    nickname: "",
    phone: "",
    address: "",
    occupation: "",
    houseForm1_5: "",
    houseFormL6_U6: "",
    positionsHeld: "",
    achievements: "",
    iceName: "",
    icePhone: "",
    iceEmail: "",
    iceAddress: "",
    iceRelationship: ""
  })

  // Admin state for dues/donations
  const [allMembers, setAllMembers] = useState<any[]>([])
  const [selectedMember, setSelectedMember] = useState<string>("")
  const [memberDues, setMemberDues] = useState<any[]>([])
  const [memberDonations, setMemberDonations] = useState<any[]>([])
  const [showAddDues, setShowAddDues] = useState(false)
  const [showAddDonation, setShowAddDonation] = useState(false)
  const [newDues, setNewDues] = useState({ year: new Date().getFullYear(), amount: 0, status: "paid" })
  const [newDonation, setNewDonation] = useState({ amount: 0, purpose: "", isSchoolDonation: false })
  
  // Admin member management state
  const [viewingMember, setViewingMember] = useState<any>(null)
  const [editingMember, setEditingMember] = useState<any>(null)
  const [showAddMember, setShowAddMember] = useState(false)
  const [memberFormData, setMemberFormData] = useState<any>({
    firstName: "", middleName: "", lastName: "", nickname: "", phone: "", email: "",
    address: "", occupation: "", houseForm1_5: "", houseFormL6_U6: "",
    positionsHeld: "", achievements: "", iceName: "", icePhone: "",
    iceEmail: "", iceAddress: "", iceRelationship: ""
  })

  // User's own dues/donations
  const [myDues, setMyDues] = useState<any[]>([])
  const [myDonations, setMyDonations] = useState<any[]>([])

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    if (user) {
      loadProfile()
      if (user.userRole?.role === "ADMIN") {
        loadAllMembers()
      } else {
        loadMyDuesAndDonations()
        loadAllMembers() // Load members list for sidebar
      }
    }
  }, [user])

  const loadProfile = async () => {
    if (!user) return
    try {
      const result = await lumi.entities.members.list({
        filter: { userId: user.userId },
        limit: 1
      })
      if (result.list.length > 0) {
        const memberProfile = result.list[0]
        setProfile(memberProfile)
        setFormData({
          firstName: memberProfile.firstName || "",
          middleName: memberProfile.middleName || "",
          lastName: memberProfile.lastName || "",
          nickname: memberProfile.nickname || "",
          phone: memberProfile.phone || "",
          address: memberProfile.address || "",
          occupation: memberProfile.occupation || "",
          houseForm1_5: memberProfile.houseForm1_5 || "",
          houseFormL6_U6: memberProfile.houseFormL6_U6 || "",
          positionsHeld: memberProfile.positionsHeld || "",
          achievements: memberProfile.achievements || "",
          iceName: memberProfile.iceName || "",
          icePhone: memberProfile.icePhone || "",
          iceEmail: memberProfile.iceEmail || "",
          iceAddress: memberProfile.iceAddress || "",
          iceRelationship: memberProfile.iceRelationship || ""
        })
      }
    } catch (error) {
      console.error("Failed to load profile:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadAllMembers = async () => {
    try {
      const result = await lumi.entities.members.list({})
      const sortedMembers = result.list.sort((a: any, b: any) => {
        const lastNameA = (a.lastName || "").toLowerCase()
        const lastNameB = (b.lastName || "").toLowerCase()
        return lastNameA.localeCompare(lastNameB)
      })
      setAllMembers(sortedMembers)
    } catch (error) {
      console.error("Failed to load members:", error)
    }
  }

  const loadMemberDuesAndDonations = async (memberId: string) => {
    try {
      const duesResult = await lumi.entities.dues.list({
        filter: { memberId },
        sort: { year: -1 }
      })
      const donationsResult = await lumi.entities.donations.list({
        filter: { memberId },
        sort: { donationDate: -1 }
      })
      setMemberDues(duesResult.list)
      setMemberDonations(donationsResult.list)
    } catch (error) {
      console.error("Failed to load dues/donations:", error)
    }
  }

  const loadMyDuesAndDonations = async () => {
    if (!profile?._id) return
    try {
      const duesResult = await lumi.entities.dues.list({
        filter: { memberId: profile._id },
        sort: { year: -1 }
      })
      const donationsResult = await lumi.entities.donations.list({
        filter: { memberId: profile._id },
        sort: { donationDate: -1 }
      })
      setMyDues(duesResult.list)
      setMyDonations(donationsResult.list)
    } catch (error) {
      console.error("Failed to load dues/donations:", error)
    }
  }

  useEffect(() => {
    if (profile && user?.userRole?.role !== "ADMIN") {
      loadMyDuesAndDonations()
    }
  }, [profile])

  useEffect(() => {
    if (selectedMember) {
      loadMemberDuesAndDonations(selectedMember)
    }
  }, [selectedMember])

  const handleSignIn = async () => {
    try {
      await lumi.auth.signIn()
      toast.success("Successfully signed in!")
    } catch (error) {
      console.error("Sign in failed:", error)
      toast.error("Sign in failed. Please try again.")
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSaveProfile = async () => {
    if (!user) return
    setSaving(true)
    try {
      const profileData = {
        userId: user.userId,
        email: user.email,
        ...formData,
        updatedAt: new Date()
      }

      if (profile) {
        await lumi.entities.members.update(profile._id, profileData)
        toast.success("Profile updated successfully!")
      } else {
        const created = await lumi.entities.members.create({
          ...profileData,
          createdAt: new Date()
        })
        setProfile(created)
        toast.success("Profile created successfully!")
      }
      loadProfile()
    } catch (error) {
      console.error("Failed to save profile:", error)
      toast.error("Failed to save profile. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const handleAddDues = async () => {
    if (!selectedMember) return
    try {
      await lumi.entities.dues.create({
        memberId: selectedMember,
        year: Number(newDues.year),
        amount: Number(newDues.amount),
        status: newDues.status,
        paidDate: newDues.status === "paid" ? new Date() : undefined,
        createdAt: new Date()
      })
      toast.success("Dues record added!")
      setShowAddDues(false)
      setNewDues({ year: new Date().getFullYear(), amount: 0, status: "paid" })
      loadMemberDuesAndDonations(selectedMember)
    } catch (error) {
      console.error("Failed to add dues:", error)
      toast.error("Failed to add dues record.")
    }
  }

  const handleAddDonation = async () => {
    if (!selectedMember) return
    try {
      await lumi.entities.donations.create({
        memberId: selectedMember,
        amount: Number(newDonation.amount),
        purpose: newDonation.purpose,
        isSchoolDonation: newDonation.isSchoolDonation,
        donationDate: new Date(),
        createdAt: new Date()
      })
      toast.success("Donation record added!")
      setShowAddDonation(false)
      setNewDonation({ amount: 0, purpose: "", isSchoolDonation: false })
      loadMemberDuesAndDonations(selectedMember)
    } catch (error) {
      console.error("Failed to add donation:", error)
      toast.error("Failed to add donation record.")
    }
  }

  const handleDeleteDues = async (duesId: string) => {
    if (!confirm("Are you sure you want to delete this dues record?")) return
    try {
      await lumi.entities.dues.delete(duesId)
      toast.success("Dues record deleted!")
      loadMemberDuesAndDonations(selectedMember)
    } catch (error) {
      console.error("Failed to delete dues:", error)
      toast.error("Failed to delete dues record.")
    }
  }

  const handleDeleteDonation = async (donationId: string) => {
    if (!confirm("Are you sure you want to delete this donation record?")) return
    try {
      await lumi.entities.donations.delete(donationId)
      toast.success("Donation record deleted!")
      loadMemberDuesAndDonations(selectedMember)
    } catch (error) {
      console.error("Failed to delete donation:", error)
      toast.error("Failed to delete donation record.")
    }
  }

  const handleViewMember = (member: any) => {
    setViewingMember(member)
  }

  const handleEditMember = (member: any) => {
    setEditingMember(member)
    setMemberFormData({
      firstName: member.firstName || "",
      middleName: member.middleName || "",
      lastName: member.lastName || "",
      nickname: member.nickname || "",
      phone: member.phone || "",
      email: member.email || "",
      address: member.address || "",
      occupation: member.occupation || "",
      houseForm1_5: member.houseForm1_5 || "",
      houseFormL6_U6: member.houseFormL6_U6 || "",
      positionsHeld: member.positionsHeld || "",
      achievements: member.achievements || "",
      iceName: member.iceName || "",
      icePhone: member.icePhone || "",
      iceEmail: member.iceEmail || "",
      iceAddress: member.iceAddress || "",
      iceRelationship: member.iceRelationship || ""
    })
  }

  const handleSaveMember = async () => {
    try {
      if (editingMember) {
        await lumi.entities.members.update(editingMember._id, memberFormData)
        toast.success("Member updated successfully!")
        setEditingMember(null)
      } else {
        await lumi.entities.members.create({
          ...memberFormData,
          userId: `manual_${Date.now()}`,
          createdAt: new Date(),
          updatedAt: new Date()
        })
        toast.success("Member added successfully!")
        setShowAddMember(false)
      }
      setMemberFormData({
        firstName: "", middleName: "", lastName: "", nickname: "", phone: "", email: "",
        address: "", occupation: "", houseForm1_5: "", houseFormL6_U6: "",
        positionsHeld: "", achievements: "", iceName: "", icePhone: "",
        iceEmail: "", iceAddress: "", iceRelationship: ""
      })
      loadAllMembers()
    } catch (error) {
      console.error("Failed to save member:", error)
      toast.error("Failed to save member.")
    }
  }

  const handleDeleteMember = async (memberId: string) => {
    if (!confirm("Are you sure you want to delete this member? This will also delete their dues and donations.")) return
    try {
      await lumi.entities.members.delete(memberId)
      toast.success("Member deleted successfully!")
      loadAllMembers()
      setViewingMember(null)
    } catch (error) {
      console.error("Failed to delete member:", error)
      toast.error("Failed to delete member.")
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen py-16 bg-gradient-to-br from-black via-[#1A1A1A] to-black">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Lock size={64} className="text-[#DC143C] mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Members Portal</h1>
            <p className="text-gray-400 text-lg">Sign in to access the members area</p>
          </div>

          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Welcome Back!</h2>
            <p className="text-gray-400 mb-8">
              Access your profile, update your information, and connect with fellow MOBA91 alumni.
            </p>

            <button
              onClick={handleSignIn}
              className="px-8 py-4 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all transform hover:scale-105 flex items-center justify-center space-x-2 mx-auto"
            >
              <User size={20} />
              <span>Sign In to Continue</span>
            </button>

            <div className="mt-8 pt-8 border-t border-[#DC143C]/20">
              <div className="flex items-start space-x-3 text-left text-sm text-gray-400">
                <Info size={20} className="text-[#DC143C] flex-shrink-0 mt-0.5" />
                <p>
                  First time here? Contact the MOBA91 organizing committee to get your member account set up.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen py-16 bg-gradient-to-br from-black via-[#1A1A1A] to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading profile...</p>
        </div>
      </div>
    )
  }

  // Admin View
  if (user.userRole?.role === "ADMIN") {
    return (
      <div className="min-h-screen py-16 bg-gradient-to-br from-black via-[#1A1A1A] to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck size={40} className="text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">Manage Members, Dues & Donations</p>
          </div>

          {/* Member Profile Section */}
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6 text-[#DC143C]">My Profile</h2>
            <MemberProfileForm
              formData={formData}
              handleInputChange={handleInputChange}
              handleSaveProfile={handleSaveProfile}
              saving={saving}
              userEmail={user.email}
            />
          </div>

          {/* Members List Management */}
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#DC143C]">All Members</h2>
              <button
                onClick={() => setShowAddMember(true)}
                className="px-6 py-3 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add Member</span>
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto custom-scrollbar">
              {allMembers.map((member) => (
                <div key={member._id} className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-4 hover:border-[#DC143C]/50 transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <button
                        onClick={() => handleViewMember(member)}
                        className="text-left hover:text-[#DC143C] transition-colors"
                      >
                        <h3 className="font-bold text-lg">{member.firstName} {member.lastName}</h3>
                        {member.nickname && <p className="text-sm text-gray-400">"{member.nickname}"</p>}
                        <p className="text-xs text-gray-500 mt-1">{member.email}</p>
                      </button>
                    </div>
                  </div>
                  {member.houseForm1_5 && (
                    <p className="text-sm text-[#DC143C] mb-3">{member.houseForm1_5}</p>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditMember(member)}
                      className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm font-semibold transition-all flex items-center justify-center space-x-1"
                    >
                      <Edit size={14} />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDeleteMember(member._id)}
                      className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm font-semibold transition-all flex items-center justify-center space-x-1"
                    >
                      <Trash2 size={14} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dues & Donations Management */}
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-6 text-[#DC143C]">Manage Dues & Donations</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Select Member</label>
              <select
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white"
              >
                <option value="">Choose a member...</option>
                {allMembers.map((member) => (
                  <option key={member._id} value={member._id}>
                    {member.firstName} {member.lastName} ({member.email})
                  </option>
                ))}
              </select>
            </div>

            {selectedMember && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Dues Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold flex items-center space-x-2">
                      <DollarSign size={24} className="text-[#DC143C]" />
                      <span>Dues Records</span>
                    </h3>
                    <button
                      onClick={() => setShowAddDues(true)}
                      className="px-4 py-2 bg-[#DC143C] hover:bg-red-600 rounded-lg text-sm font-semibold transition-all flex items-center space-x-2"
                    >
                      <Plus size={16} />
                      <span>Add</span>
                    </button>
                  </div>

                  {showAddDues && (
                    <div className="bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold mb-3">Add Dues Record</h4>
                      <div className="space-y-3">
                        <input
                          type="number"
                          placeholder="Year"
                          value={newDues.year}
                          onChange={(e) => setNewDues({ ...newDues, year: Number(e.target.value) })}
                          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded text-white"
                        />
                        <input
                          type="number"
                          placeholder="Amount"
                          value={newDues.amount}
                          onChange={(e) => setNewDues({ ...newDues, amount: Number(e.target.value) })}
                          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded text-white"
                        />
                        <select
                          value={newDues.status}
                          onChange={(e) => setNewDues({ ...newDues, status: e.target.value })}
                          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded text-white"
                        >
                          <option value="paid">Paid</option>
                          <option value="pending">Pending</option>
                          <option value="overdue">Overdue</option>
                        </select>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleAddDues}
                            className="flex-1 px-4 py-2 bg-[#DC143C] hover:bg-red-600 rounded font-semibold"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setShowAddDues(false)}
                            className="flex-1 px-4 py-2 bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {memberDues.length === 0 ? (
                      <p className="text-gray-400 text-sm">No dues records yet</p>
                    ) : (
                      memberDues.map((dues) => (
                        <div key={dues._id} className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-semibold">Year {dues.year}</div>
                              <div className="text-2xl font-bold text-[#DC143C]">${dues.amount}</div>
                            </div>
                            <button
                              onClick={() => handleDeleteDues(dues._id)}
                              className="p-2 hover:bg-red-500/20 rounded transition-all"
                            >
                              <Trash2 size={16} className="text-red-400" />
                            </button>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              dues.status === "paid" ? "bg-green-500/20 text-green-400" :
                              dues.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                              "bg-red-500/20 text-red-400"
                            }`}>
                              {dues.status.toUpperCase()}
                            </span>
                            {dues.paidDate && (
                              <span className="text-xs text-gray-400">
                                Paid: {new Date(dues.paidDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Donations Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold flex items-center space-x-2">
                      <Calendar size={24} className="text-[#DC143C]" />
                      <span>Donations</span>
                    </h3>
                    <button
                      onClick={() => setShowAddDonation(true)}
                      className="px-4 py-2 bg-[#DC143C] hover:bg-red-600 rounded-lg text-sm font-semibold transition-all flex items-center space-x-2"
                    >
                      <Plus size={16} />
                      <span>Add</span>
                    </button>
                  </div>

                  {showAddDonation && (
                    <div className="bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold mb-3">Add Donation Record</h4>
                      <div className="space-y-3">
                        <input
                          type="number"
                          placeholder="Amount"
                          value={newDonation.amount}
                          onChange={(e) => setNewDonation({ ...newDonation, amount: Number(e.target.value) })}
                          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded text-white"
                        />
                        <input
                          type="text"
                          placeholder="Purpose (e.g., 40th Anniversary Fund)"
                          value={newDonation.purpose}
                          onChange={(e) => setNewDonation({ ...newDonation, purpose: e.target.value })}
                          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded text-white"
                        />
                        <label className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={newDonation.isSchoolDonation}
                            onChange={(e) => setNewDonation({ ...newDonation, isSchoolDonation: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span>Donation to Mfantsipim School</span>
                        </label>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleAddDonation}
                            className="flex-1 px-4 py-2 bg-[#DC143C] hover:bg-red-600 rounded font-semibold"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setShowAddDonation(false)}
                            className="flex-1 px-4 py-2 bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {memberDonations.length === 0 ? (
                      <p className="text-gray-400 text-sm">No donation records yet</p>
                    ) : (
                      memberDonations.map((donation) => (
                        <div key={donation._id} className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="text-2xl font-bold text-[#DC143C]">${donation.amount}</div>
                              {donation.purpose && <div className="text-sm text-gray-400 mt-1">{donation.purpose}</div>}
                            </div>
                            <button
                              onClick={() => handleDeleteDonation(donation._id)}
                              className="p-2 hover:bg-red-500/20 rounded transition-all"
                            >
                              <Trash2 size={16} className="text-red-400" />
                            </button>
                          </div>
                          <div className="flex items-center space-x-2 text-xs">
                            {donation.isSchoolDonation && (
                              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">School Donation</span>
                            )}
                            <span className="text-gray-400">
                              {new Date(donation.donationDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Regular User View
  return (
    <div className="min-h-screen py-16 bg-gradient-to-br from-black via-[#1A1A1A] to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserCheck size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-2">Welcome, {user.userName}!</h1>
          <p className="text-gray-400">Member Portal</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Members List Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-6 sticky top-20">
              <h3 className="text-lg font-bold mb-4 text-[#DC143C]">Registered Members</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto custom-scrollbar">
                {allMembers.map((member) => (
                  <div key={member._id} className="p-3 bg-[#2A2A2A] rounded-lg hover:bg-[#3A3A3A] transition-all border border-[#DC143C]/10 hover:border-[#DC143C]/30">
                    <div className="font-semibold text-sm text-white">
                      {member.firstName} {member.lastName}
                    </div>
                    {member.nickname && (
                      <div className="text-xs text-gray-400">"{member.nickname}"</div>
                    )}
                    {member.houseForm1_5 && (
                      <div className="text-xs text-[#DC143C] mt-1">{member.houseForm1_5.split(" ")[0]}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {/* Member Profile Form */}
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-6 text-[#DC143C]">Member Profile</h2>
              <MemberProfileForm
                formData={formData}
                handleInputChange={handleInputChange}
                handleSaveProfile={handleSaveProfile}
                saving={saving}
                userEmail={user.email}
              />
            </div>

        {/* My Dues & Donations */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* My Dues */}
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4 text-[#DC143C] flex items-center space-x-2">
              <DollarSign size={24} />
              <span>My Dues</span>
            </h2>
            <div className="space-y-3">
              {myDues.length === 0 ? (
                <p className="text-gray-400 text-sm">No dues records yet</p>
              ) : (
                myDues.map((dues) => (
                  <div key={dues._id} className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="font-semibold">Year {dues.year}</div>
                      <div className="text-xl font-bold text-[#DC143C]">${dues.amount}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        dues.status === "paid" ? "bg-green-500/20 text-green-400" :
                        dues.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-red-500/20 text-red-400"
                      }`}>
                        {dues.status.toUpperCase()}
                      </span>
                      {dues.paidDate && (
                        <span className="text-xs text-gray-400">
                          {new Date(dues.paidDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* My Donations */}
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4 text-[#DC143C] flex items-center space-x-2">
              <Calendar size={24} />
              <span>My Donations</span>
            </h2>
            <div className="space-y-3">
              {myDonations.length === 0 ? (
                <p className="text-gray-400 text-sm">No donation records yet</p>
              ) : (
                myDonations.map((donation) => (
                  <div key={donation._id} className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-4">
                    <div className="text-xl font-bold text-[#DC143C] mb-1">${donation.amount}</div>
                    {donation.purpose && <div className="text-sm text-gray-400 mb-2">{donation.purpose}</div>}
                    <div className="flex items-center space-x-2 text-xs">
                      {donation.isSchoolDonation && (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">School Donation</span>
                      )}
                      <span className="text-gray-400">
                        {new Date(donation.donationDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MemberProfileForm({ formData, handleInputChange, handleSaveProfile, saving, userEmail }: any) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-6">
        <FormField label="First Name" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
        <FormField label="Middle Name" name="middleName" value={formData.middleName} onChange={handleInputChange} />
        <FormField label="Last Name" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
        <FormField label="Nickname" name="nickname" value={formData.nickname} onChange={handleInputChange} />
        <FormField label="Email" name="email" value={userEmail} disabled />
        <FormField label="Phone" name="phone" value={formData.phone} onChange={handleInputChange} />
        <FormField label="Address" name="address" value={formData.address} onChange={handleInputChange} fullWidth />
        <FormField label="Occupation" name="occupation" value={formData.occupation} onChange={handleInputChange} />
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            House (Form 1-5)
          </label>
          <select 
            name="houseForm1_5"
            value={formData.houseForm1_5}
            onChange={handleInputChange}
            className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]"
          >
            <option value="">Select house</option>
            <option value="Abruquah-Monney (Upper)">Abruquah-Monney (Upper)</option>
            <option value="Abruquah-Monney (Lower)">Abruquah-Monney (Lower)</option>
            <option value="Balmer-Acquah (Upper)">Balmer-Acquah (Upper)</option>
            <option value="Balmer-Acquah (Lower)">Balmer-Acquah (Lower)</option>
            <option value="Bartels-Sneath (Upper)">Bartels-Sneath (Upper)</option>
            <option value="Bartels-Sneath (Middle)">Bartels-Sneath (Middle)</option>
            <option value="Bartels-Sneath (Lower)">Bartels-Sneath (Lower)</option>
            <option value="Brandful-Dontwi (Upper)">Brandful-Dontwi (Upper)</option>
            <option value="Brandful-Dontwi (Lower)">Brandful-Dontwi (Lower)</option>
            <option value="Freeman-Aggrey (Upper)">Freeman-Aggrey (Upper)</option>
            <option value="Freeman-Aggrey (Lower)">Freeman-Aggrey (Lower)</option>
            <option value="Lockhart-Schweitzer (Upper)">Lockhart-Schweitzer (Upper)</option>
            <option value="Lockhart-Schweitzer (Lower)">Lockhart-Schweitzer (Lower)</option>
            <option value="Pickard-Parker (Upper)">Pickard-Parker (Upper)</option>
            <option value="Pickard-Parker (Lower)">Pickard-Parker (Lower)</option>
            <option value="Sarbah-Picot (Upper)">Sarbah-Picot (Upper)</option>
            <option value="Sarbah-Picot (Lower)">Sarbah-Picot (Lower)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">House (L6-U6)</label>
          <select 
            name="houseFormL6_U6"
            value={formData.houseFormL6_U6}
            onChange={handleInputChange}
            className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]"
          >
            <option value="">Select house</option>
            <option value="Abruquah-Monney (Upper)">Abruquah-Monney (Upper)</option>
            <option value="Abruquah-Monney (Lower)">Abruquah-Monney (Lower)</option>
            <option value="Balmer-Acquah (Upper)">Balmer-Acquah (Upper)</option>
            <option value="Balmer-Acquah (Lower)">Balmer-Acquah (Lower)</option>
            <option value="Bartels-Sneath (Upper)">Bartels-Sneath (Upper)</option>
            <option value="Bartels-Sneath (Middle)">Bartels-Sneath (Middle)</option>
            <option value="Bartels-Sneath (Lower)">Bartels-Sneath (Lower)</option>
            <option value="Brandful-Dontwi (Upper)">Brandful-Dontwi (Upper)</option>
            <option value="Brandful-Dontwi (Lower)">Brandful-Dontwi (Lower)</option>
            <option value="Freeman-Aggrey (Upper)">Freeman-Aggrey (Upper)</option>
            <option value="Freeman-Aggrey (Lower)">Freeman-Aggrey (Lower)</option>
            <option value="Lockhart-Schweitzer (Upper)">Lockhart-Schweitzer (Upper)</option>
            <option value="Lockhart-Schweitzer (Lower)">Lockhart-Schweitzer (Lower)</option>
            <option value="Pickard-Parker (Upper)">Pickard-Parker (Upper)</option>
            <option value="Pickard-Parker (Lower)">Pickard-Parker (Lower)</option>
            <option value="Sarbah-Picot (Upper)">Sarbah-Picot (Upper)</option>
            <option value="Sarbah-Picot (Lower)">Sarbah-Picot (Lower)</option>
          </select>
        </div>

        <FormField label="Positions Held" name="positionsHeld" value={formData.positionsHeld} onChange={handleInputChange} fullWidth />
        <FormField label="Achievements" name="achievements" value={formData.achievements} onChange={handleInputChange} fullWidth />
      </div>

      {/* ICE (In Case of Emergency) Section */}
      <div className="mt-8 pt-8 border-t border-[#DC143C]/30">
        <h3 className="text-xl font-bold mb-4 text-[#DC143C]">Emergency Contact Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <FormField label="ICE Name" name="iceName" value={formData.iceName} onChange={handleInputChange} fullWidth />
          <FormField label="ICE Phone Number" name="icePhone" value={formData.icePhone} onChange={handleInputChange} />
          <FormField label="ICE Email Address" name="iceEmail" value={formData.iceEmail} onChange={handleInputChange} />
          <FormField label="Relationship" name="iceRelationship" value={formData.iceRelationship} onChange={handleInputChange} />
          <FormField label="ICE Address" name="iceAddress" value={formData.iceAddress} onChange={handleInputChange} fullWidth />
        </div>
      </div>

      <button
        onClick={handleSaveProfile}
        disabled={saving}
        className="mt-8 px-8 py-3 bg-[#DC143C] hover:bg-red-600 disabled:bg-[#2A2A2A] disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg font-semibold transition-all flex items-center space-x-2"
      >
        <Save size={20} />
        <span>{saving ? "Saving..." : "Save Profile"}</span>
      </button>
    </>
  )
}

function FormField({ label, name, value, onChange, disabled, fullWidth, required }: any) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label} {required && <span className="text-[#DC143C]">*</span>}
      </label>
      <input
        type="text"
        name={name}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C] disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
  )
}

export default Members

function MemberForm({ formData, onChange }: any) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <FormField label="First Name" name="firstName" value={formData.firstName} onChange={onChange} required />
      <FormField label="Middle Name" name="middleName" value={formData.middleName} onChange={onChange} />
      <FormField label="Last Name" name="lastName" value={formData.lastName} onChange={onChange} required />
      <FormField label="Nickname" name="nickname" value={formData.nickname} onChange={onChange} />
      <FormField label="Email" name="email" value={formData.email} onChange={onChange} required />
      <FormField label="Phone" name="phone" value={formData.phone} onChange={onChange} />
      <FormField label="Address" name="address" value={formData.address} onChange={onChange} fullWidth />
      <FormField label="Occupation" name="occupation" value={formData.occupation} onChange={onChange} />
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">House (Form 1-5)</label>
        <select 
          name="houseForm1_5"
          value={formData.houseForm1_5}
          onChange={onChange}
          className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]"
        >
          <option value="">Select house</option>
          <option value="Abruquah-Monney (Upper)">Abruquah-Monney (Upper)</option>
          <option value="Abruquah-Monney (Lower)">Abruquah-Monney (Lower)</option>
          <option value="Balmer-Acquah (Upper)">Balmer-Acquah (Upper)</option>
          <option value="Balmer-Acquah (Lower)">Balmer-Acquah (Lower)</option>
          <option value="Bartels-Sneath (Upper)">Bartels-Sneath (Upper)</option>
          <option value="Bartels-Sneath (Middle)">Bartels-Sneath (Middle)</option>
          <option value="Bartels-Sneath (Lower)">Bartels-Sneath (Lower)</option>
          <option value="Brandful-Dontwi (Upper)">Brandful-Dontwi (Upper)</option>
          <option value="Brandful-Dontwi (Lower)">Brandful-Dontwi (Lower)</option>
          <option value="Freeman-Aggrey (Upper)">Freeman-Aggrey (Upper)</option>
          <option value="Freeman-Aggrey (Lower)">Freeman-Aggrey (Lower)</option>
          <option value="Lockhart-Schweitzer (Upper)">Lockhart-Schweitzer (Upper)</option>
          <option value="Lockhart-Schweitzer (Lower)">Lockhart-Schweitzer (Lower)</option>
          <option value="Pickard-Parker (Upper)">Pickard-Parker (Upper)</option>
          <option value="Pickard-Parker (Lower)">Pickard-Parker (Lower)</option>
          <option value="Sarbah-Picot (Upper)">Sarbah-Picot (Upper)</option>
          <option value="Sarbah-Picot (Lower)">Sarbah-Picot (Lower)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">House (L6-U6)</label>
        <select 
          name="houseFormL6_U6"
          value={formData.houseFormL6_U6}
          onChange={onChange}
          className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]"
        >
          <option value="">Select house</option>
          <option value="Abruquah-Monney (Upper)">Abruquah-Monney (Upper)</option>
          <option value="Abruquah-Monney (Lower)">Abruquah-Monney (Lower)</option>
          <option value="Balmer-Acquah (Upper)">Balmer-Acquah (Upper)</option>
          <option value="Balmer-Acquah (Lower)">Balmer-Acquah (Lower)</option>
          <option value="Bartels-Sneath (Upper)">Bartels-Sneath (Upper)</option>
          <option value="Bartels-Sneath (Middle)">Bartels-Sneath (Middle)</option>
          <option value="Bartels-Sneath (Lower)">Bartels-Sneath (Lower)</option>
          <option value="Brandful-Dontwi (Upper)">Brandful-Dontwi (Upper)</option>
          <option value="Brandful-Dontwi (Lower)">Brandful-Dontwi (Lower)</option>
          <option value="Freeman-Aggrey (Upper)">Freeman-Aggrey (Upper)</option>
          <option value="Freeman-Aggrey (Lower)">Freeman-Aggrey (Lower)</option>
          <option value="Lockhart-Schweitzer (Upper)">Lockhart-Schweitzer (Upper)</option>
          <option value="Lockhart-Schweitzer (Lower)">Lockhart-Schweitzer (Lower)</option>
          <option value="Pickard-Parker (Upper)">Pickard-Parker (Upper)</option>
          <option value="Pickard-Parker (Lower)">Pickard-Parker (Lower)</option>
          <option value="Sarbah-Picot (Upper)">Sarbah-Picot (Upper)</option>
          <option value="Sarbah-Picot (Lower)">Sarbah-Picot (Lower)</option>
        </select>
      </div>

      <FormField label="Positions Held" name="positionsHeld" value={formData.positionsHeld} onChange={onChange} fullWidth />
      <FormField label="Achievements" name="achievements" value={formData.achievements} onChange={onChange} fullWidth />
      
      <div className="md:col-span-2 pt-4 border-t border-[#DC143C]/30">
        <h3 className="text-lg font-bold text-[#DC143C] mb-4">Emergency Contact Information</h3>
      </div>
      
      <FormField label="ICE Name" name="iceName" value={formData.iceName} onChange={onChange} fullWidth />
      <FormField label="ICE Phone" name="icePhone" value={formData.icePhone} onChange={onChange} />
      <FormField label="ICE Email" name="iceEmail" value={formData.iceEmail} onChange={onChange} />
      <FormField label="Relationship" name="iceRelationship" value={formData.iceRelationship} onChange={onChange} />
      <FormField label="ICE Address" name="iceAddress" value={formData.iceAddress} onChange={onChange} fullWidth />
    </div>
  )
}
